<template>
  <v-list-item
    v-if="rail || !menuItem.children"
    :title="rail ? undefined : $t(menuItem.title)"
    link
    :to="menuItem.to"
    :disabled="menuItem.disabled"
    :style="rail ? `padding: 4px 10px` : undefined"
    :exact="menuItem.exact"
  >
    <template #prepend>
      <div v-if="rail" style="margin-inline-end: 6px; margin-inline-start: 6px">
        <active-dot :dot-active="hasChildrenWithBadge">
          <v-icon class="mt-1 mb-4" :icon="menuItem.icon" />
        </active-dot>
        <div
          class="text-body-small text-center position-absolute font-weight-medium text-no-wrap"
          style="font-size: 9px !important; bottom: 0; left: 0; right: 0"
        >
          {{ $te(menuItem.title) ? $t(menuItem.title) : menuItem.title }}
        </div>
      </div>
      <active-dot v-else-if="menuItem.icon" :dot-active="hasChildrenWithBadge">
        <v-icon :icon="menuItem.icon" />
      </active-dot>
    </template>
  </v-list-item>

  <v-list-group v-else>
    <template #activator="{ props: itemProps }">
      <v-list-item
        v-bind="itemProps"
        :prepend-icon="menuItem.icon"
        :title="$te(menuItem.title) ? $t(menuItem.title) : menuItem.title"
        :disabled="menuItem.disabled"
      >
        <template #prepend>
          <active-dot :dot-active="hasChildrenWithBadge">
            <v-icon :icon="menuItem.icon" />
          </active-dot>
        </template>
      </v-list-item>
    </template>

    <template #default>
      <v-list class="pa-0" density="comfortable" :prepend-gap="5">
        <template v-if="!menuItem.hideDropdownTitle">
          <v-list-item
            :title="menuItem.dropdownTitle ? $t(menuItem.dropdownTitle) : `${$t('all')} ${$t(menuItem.title)}`"
            link
            :active="menuItem.activeMethod ? menuItem.activeMethod() : undefined"
            :to="menuItem.to"
            :disabled="menuItem.disabled"
          >
            <template #prepend>
              <v-avatar size="24" color="transparent" style="margin-inline-end: 16px" class="text-white">
                <v-icon icon="fiber_manual_record" size="6px" />
              </v-avatar>
            </template>
          </v-list-item>

          <v-divider class="mx-4" />
        </template>

        <v-list-item
          v-for="child in menuItem.children"
          :key="child.title"
          link
          :active="child.activeMethod ? child.activeMethod() : undefined"
          :to="child.to"
          :disabled="child.disabled"
        >
          <template #prepend>
            <v-avatar
              v-if="child.icon || child.iconLabel"
              size="24"
              variant="elevated"
              :color="child.color || 'primary'"
              :style="`background-color: ${child.color}; margin-inline-end: 16px; border: 1px solid rgba(255, 255, 255, 0.5)`"
              class="text-white"
              :rounded="child.roundedAvatar || undefined"
            >
              <v-icon v-if="child.icon" size="x-small" :icon="child.icon" />
              <span v-else class="text-body-small font-weight-medium">{{ child.iconLabel }}</span>
            </v-avatar>

            <v-avatar v-else size="24" style="margin-inline-end: 16px" color="transparent" class="text-white">
              <v-icon icon="fiber_manual_record" size="6px" />
            </v-avatar>
          </template>
          <template #title>
            <div class="d-flex align-center ga-1">
              <span>
                {{ $te(child.title) ? $t(child.title) : child.title }}
              </span>
            </div>
          </template>
          <template #append>
            <v-tooltip v-if="child.badge" location="top" :disabled="!child.badge.text">
              <template #activator="{ props: itemProps }">
                <v-badge v-bind="itemProps" :content="child.badge.itemCount" color="error" inline />
              </template>

              <span>{{ child.badge.text }}</span>
            </v-tooltip>
          </template>
        </v-list-item>
      </v-list>
    </template>
  </v-list-group>
</template>

<script setup lang="ts">
export interface MenuItem {
  title: string
  icon?: string
  badge?: {
    badgeColor: string
    itemCount: number
    text?: string
  }
  iconLabel?: string
  color?: string
  roundedAvatar?: boolean
  to: string
  disabled?: boolean
  children?: Array<MenuItem>
  dropdownTitle?: string
  hideDropdownTitle?: boolean
  exact?: boolean
  activeMethod?: () => boolean | undefined
}

const props = defineProps<{
  menuItem: MenuItem
  rail?: boolean
}>()
const { menuItem, rail } = toRefs(props)

const hasChildrenWithBadge = computed(() => !!props.menuItem.children?.filter((child) => child.badge).length)
</script>
